// PR-MAY14-SMART-PLATE-SEARCH — shared matcher for vehicle pickers.
//
// Plate formats in the wild:
//   "3675-KRA"        — DB canonical (digits-first, separator)
//   "KRA-3675"        — reverse-order shorthand operators type
//   "3675 KRA"        — paper-form spelling
//   "3 6 7 5 K R A"   — PetroApp Excel export
//
// Operators searching the picker may type any of:
//   "3675"  — just the digits
//   "KRA"   — just the letters
//   "3675KRA" / "KRA3675"   — no separator
//   "375"   — fragment of digits (partial recall)
//
// matchPlateQuery(query, plate) returns true if any of:
//   1. the normalized query is a substring of the normalized plate
//   2. the normalized query is a substring of the plate with letter↔digit
//      halves swapped (so KRA3675 matches 3675KRA)
//   3. the query's letter chunk is in the plate's letters AND/OR the
//      query's digit chunk is in the plate's digits (so "37 KR" matches
//      3675-KRA)

function normalize(s: string | null | undefined): string {
  if (!s) return '';
  return s.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
}

function splitLettersDigits(s: string): { letters: string; digits: string } {
  return {
    letters: s.replace(/[0-9]/g, ''),
    digits:  s.replace(/[A-Z]/g, ''),
  };
}

function swappedHalves(plate: string): string {
  // 3675KRA → KRA3675, KRA3675 → 3675KRA, mixed → just identity.
  const m1 = plate.match(/^(\d+)([A-Z]+)$/);
  if (m1) return m1[2] + m1[1];
  const m2 = plate.match(/^([A-Z]+)(\d+)$/);
  if (m2) return m2[2] + m2[1];
  return plate;
}

/**
 * True if `query` should match `plate`. Both inputs may contain
 * separators / spaces / mixed case — they're normalized internally.
 */
export function matchPlateQuery(query: string, plate: string | null | undefined): boolean {
  return scorePlateQuery(query, plate) > 0;
}

/**
 * PR-MAY15-LEDGERS-V3 item 2 — same as matchPlateQuery but returns an
 * integer score. Higher = better match. Used by pickers to RANK
 * plate-matching results above name / code matches.
 *
 *   exact equality          → 100
 *   plate startsWith query  →  80
 *   plate contains query    →  60
 *   swapped halves contains →  40
 *   half-by-half             →  20
 *   no match                 →   0
 */
export function scorePlateQuery(query: string, plate: string | null | undefined): number {
  const q = normalize(query);
  if (!q) return 1; // empty query matches everything but with low score
  const p = normalize(plate);
  if (!p) return 0;

  if (p === q) return 100;
  if (p.startsWith(q)) return 80;
  if (p.includes(q)) return 60;
  if (swappedHalves(p).includes(q)) return 40;
  const { letters: ql, digits: qd } = splitLettersDigits(q);
  const { letters: pl, digits: pd } = splitLettersDigits(p);
  if (ql && qd && pl.includes(ql) && pd.includes(qd)) return 20;
  if (!qd && ql && pl.includes(ql)) return 20;
  if (!ql && qd && pd.includes(qd)) return 20;
  return 0;
}
